import React from 'react'
import { Field, Form, Formik } from 'formik'
import { Card, Element, Loader } from 'react-bulma-components'
import AutocompleteField from '../../../form/fields/autocomplete'
import { buildListAsInputOptions } from '../../../../utils/forms'
import { useAvailableWorkersQuery } from '../../../../queries/users'
import { WorkersRequest } from '../../../../api/workers-requests'
import { AvailableWorker } from '../../../../api/users'
import { RequestButton } from '../../../request-components/request-components'
import { useAssignWorkerToPost } from '../../../../queries/workers-requests'
import useStore from '../../../../store'
import Protected from '../../../protected/protected'
import {
  checkAvailibilityInPeriod,
  getWorkerAbsentLabel,
  getUserLabel,
} from '../../../../utils/users'
import WorkerLabel from '../../../texts/worker-label'
import { UserInputLabel } from '../../../missions/mission-form'
import { compareDocsById } from '../../../../utils/documents'
import InputField from '../../../form/fields/input'
import { useSmicQuery } from '../../../../queries/smic'
import { getInterimAgencySettingsFromPost } from '../../../../utils/workers-requests'
import WorkerInputLabel from '../worker-input-label'

interface AssignWorkerFormProps {
  workersRequest: WorkersRequest
  postId: string
}

const AssignWorkerForm: React.FC<AssignWorkerFormProps> = ({ workersRequest, postId }) => {
  const { currentUserRole, currentUser, currentOrganization } = useStore(state => state.session)
  const availableWorkers = useAvailableWorkersQuery(
    {
      workersRequestId: workersRequest._id,
    },
    { enabled: currentUserRole === 'interimAgencyMember' },
  )

  const assignWorkerToPostMutation = useAssignWorkerToPost()
  const post = workersRequest.posts.find(post => post._id === postId)

  if (!currentUser) return <Loader />
  if (!post) return <>Something went wrong</>

  const interimAgencySettings = getInterimAgencySettingsFromPost(post, workersRequest)
  const mustAddCustomHourlyCost = !interimAgencySettings?.hourlyRateSettings.useSmic

  const smicQuery = useSmicQuery(workersRequest.missionsStart, { enabled: mustAddCustomHourlyCost })
  const smicValue = smicQuery.data?.value

  const minHourlyCost =
    smicValue && smicValue > (interimAgencySettings?.hourlyRateSettings.min ?? 0)
      ? smicValue
      : interimAgencySettings?.hourlyRateSettings.min
  const maxHourlyCost =
    smicValue && smicValue > (interimAgencySettings?.hourlyRateSettings.max ?? 0)
      ? smicValue
      : interimAgencySettings?.hourlyRateSettings.max

  return (
    <Formik
      initialValues={{
        userId: '',
        postId,
        workersRequestId: workersRequest._id.toString(),
        updatedBy: currentUser._id.toString(),
        hourlyCost: undefined,
      }}
      enableReinitialize
      validate={values => {
        const errors: any = {}
        if (!values.userId) errors.userId = 'Champ requis'
        else {
          const worker = availableWorkers.data?.find(w => w._id === values.userId)
          const workerIsAvailable =
            worker &&
            checkAvailibilityInPeriod(
              new Date(workersRequest.missionsStart),
              new Date(workersRequest.missionsEnd),
              worker.availability,
            )
          if (!workerIsAvailable && worker && currentOrganization)
            errors.userId = getWorkerAbsentLabel(worker.availability, worker)
        }

        // Validation du coût horaire si nécessaire
        if (mustAddCustomHourlyCost) {
          if (!values.hourlyCost) {
            errors.hourlyCost = 'Le coût horaire est requis'
          } else {
            if (minHourlyCost != null && values.hourlyCost < minHourlyCost) {
              errors.hourlyCost = `Le coût horaire doit être supérieur ou égal à ${minHourlyCost}€`
            } else if (maxHourlyCost != null && values.hourlyCost > maxHourlyCost) {
              errors.hourlyCost = `Le coût horaire doit être inférieur ou égal à ${maxHourlyCost}€`
            }
          }
        }

        return errors
      }}
      onSubmit={values => {
        return assignWorkerToPostMutation.mutate(values)
      }}
    >
      {props => {
        const selectedWorker = availableWorkers.data?.find(
          w => w._id.toString() === props.values.userId,
        )
        return (
          <Form>
            {availableWorkers.data && availableWorkers.data[0] ? (
              <>
                <Field
                  name="userId"
                  component={AutocompleteField}
                  autoComplete="off"
                  required
                  onlySelect
                  placeholder="Rechercher"
                  items={buildListAsInputOptions(availableWorkers.data || [], {
                    labelBuilder: (user: AvailableWorker) => getUserLabel(user),
                    renderLabel: (user: AvailableWorker) => (
                      <WorkerInputLabel user={user} post={post} workersRequest={workersRequest} />
                    ),
                    sortBy: item => typeof item.renderedLabel === 'string',
                    disabled: (user: AvailableWorker) => {
                      const employerRelation = user.employerRelations.find(relation =>
                        compareDocsById(relation.employer, workersRequest.employer),
                      )
                      return Boolean(employerRelation?.collaboration?.isInactive)
                    },
                  })}
                  disabled={
                    !availableWorkers.data ||
                    !availableWorkers.data[0] ||
                    !workersRequest.__actions.missionsCanBeCreated
                  }
                  mb={0}
                />
                {mustAddCustomHourlyCost && (
                  <Field
                    name="hourlyCost"
                    component={InputField}
                    label="Coût horaire"
                    type="number"
                    placeholder="Coût horaire"
                    help={
                      <>
                        L'EU à indiqué que le coût horaire doit être spécifié entre{' '}
                        <strong>{minHourlyCost}€</strong> et <strong>{maxHourlyCost}€</strong>
                      </>
                    }
                    required
                    min={minHourlyCost}
                    max={maxHourlyCost}
                    step={0.01}
                  />
                )}
              </>
            ) : (
              <Element
                italic
                backgroundColor="light"
                textColor="grey"
                py={2}
                px={5}
                style={{ borderRadius: 4 }}
              >
                <Protected roles={['interimAgencyMember']}>
                  Aucun <WorkerLabel /> disponible
                </Protected>
                <Protected roles={['employerMember']}>
                  Aucun <WorkerLabel /> assigné
                </Protected>
              </Element>
            )}
            {props.values.userId && (
              <>
                <br />
                {currentOrganization && selectedWorker && (
                  <Card>
                    <Card.Content>
                      <UserInputLabel user={selectedWorker} organization={currentOrganization} />
                    </Card.Content>
                  </Card>
                )}
                <br />
                <RequestButton
                  mutation={assignWorkerToPostMutation}
                  type="submit"
                  data-test="assign-worker-submit-btn"
                  disabled={assignWorkerToPostMutation.isLoading}
                >
                  Assigner
                </RequestButton>
              </>
            )}
          </Form>
        )
      }}
    </Formik>
  )
}

export default AssignWorkerForm
